import { graphqlQuery } from '@/lib/api/graphql';
import { mockNotesList } from '@/lib/demo-data/notes-list';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import type { NoteEntityType, NoteListItem } from '@/types/note-list';
import { useModeAwareQuery } from './useModeAwareQuery';

// --- GraphQL ---

interface CustomerNoteNode {
  noteId: number;
  entityType: string;
  entityId: number;
  content: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string | null;
}

interface CustomerNotesResponse {
  notes: {
    noteList: {
      edges: { node: CustomerNoteNode }[];
    };
  };
}

const CUSTOMER_CANONICAL_NOTES_QUERY = `
  query CustomerCanonicalNotes($first: Int, $sort: [NoteListSortSortByInput!], $filter: NoteListFilterInput) {
    notes {
      noteList(first: $first, sort: $sort, filter: $filter) {
        edges {
          node {
            noteId
            entityType
            entityId
            content
            createdByName
            createdAt
            updatedAt
          }
        }
      }
    }
  }
`;

// --- Types ---

export interface CustomerCanonicalNote {
  noteId: number;
  entityType: NoteEntityType;
  entityId: number;
  content: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string | null;
}

export interface CustomerCanonicalNotesResult {
  notes: CustomerCanonicalNote[];
  isLoading: boolean;
}

const SYSTEM_AUTHOR = 'System';

function toCanonicalNote(note: NoteListItem): CustomerCanonicalNote {
  return {
    noteId: note.noteId,
    entityType: note.entityType,
    entityId: note.entityId,
    content: note.content,
    createdByName: note.createdByName ?? SYSTEM_AUTHOR,
    createdAt: note.createdAt,
    updatedAt: note.updatedAt,
  };
}

function buildMockNotes(customerId: number | undefined, limit: number): CustomerCanonicalNote[] {
  return mockNotesList
    .filter((x) => customerId === undefined || x.customerId === customerId)
    .slice(0, limit)
    .map(toCanonicalNote);
}

/**
 * The customer's notes across all five note domains, read from the canonical note tables via the
 * v_Notes_List union. Customer-level timelines merge these in place of the mirrored note activities
 * so an edited note shows its current content and a deleted one disappears.
 */
export function useCustomerCanonicalNotes(customerId: number | undefined, limit: number): CustomerCanonicalNotesResult {
  const apiConfig = useApiConfig();

  const { data, isLoading } = useModeAwareQuery<CustomerCanonicalNote[], Error>(
    ['customer-canonical-notes', customerId, limit],
    async ({ signal }) => {
      if (customerId === undefined) return [];

      const response = await graphqlQuery<CustomerNotesResponse>(
        apiConfig,
        CUSTOMER_CANONICAL_NOTES_QUERY,
        {
          first: limit,
          filter: { customerId: { eq: customerId } },
          // Note ids are only unique within their own domain, so ties on the timestamp break on the
          // entity type as well — otherwise which notes make the page can change between refetches.
          sort: [
            { field: 'CreatedAt', order: 'Descending' },
            { field: 'EntityType', order: 'Descending' },
            { field: 'NoteId', order: 'Descending' },
          ],
        },
        signal,
      );

      return response.notes.noteList.edges.map(({ node }) => ({
        noteId: node.noteId,
        entityType: node.entityType as NoteEntityType,
        entityId: node.entityId,
        content: node.content ?? '',
        createdByName: node.createdByName ?? SYSTEM_AUTHOR,
        createdAt: node.createdAt,
        updatedAt: node.updatedAt,
      }));
    },
    buildMockNotes(customerId, limit),
    {
      enabled: customerId !== undefined,
      staleTime: 30_000,
      refetchOnMount: 'always',
    },
  );

  return { notes: data ?? [], isLoading };
}
